import app from 'app';
import { workPool } from '@wochitnpm/helpers';
import PictureInPictureAdapter from 'c/PictureInPictureAdapter';

export function getStageDimensions(story) {
  const assetEditorWidth = app.ASSET_EDITOR_WIDTH;
  const assetEditorHeight = app.ASSET_EDITOR_HEIGHT;
  const visibleAreaWidth = assetEditorHeight * story.get('_aspectRatio').ratio;

  return {
    assetEditorWidth,
    assetEditorHeight,
    assetEditorAR: assetEditorWidth / assetEditorHeight,
    visibleAreaWidth,
    curtainWidth: Math.round((assetEditorWidth - visibleAreaWidth) / 2),
    curtainBorder: 2,
  };
}

/**
 * Where the asset's own video sits inside the editor stage, in stage pixels.
 *
 * The stage is a fixed 688x387 canvas; the video is fitted into it by aspect
 * ratio, scaled by the crop zoom and shifted by the crop offset, which usually
 * leaves letterbox space either side. Anything that needs to express a position
 * in the video's own coordinates - object tracking does - has to go through this.
 *
 * Lives here because the stage geometry does - it is the same fit the editor uses
 * to place the asset, and object tracking needs it to express a box the user
 * dragged on the canvas in the video's own coordinates.
 *
 * @param metadata {Object} the asset's metadata, for width/height
 * @param scale {Number} agd.scale, the crop zoom
 * @param offset {Object|null} agd.offset, {top, left} as fractions of the stage
 * @param stageDimensions {Object} see getStageDimensions
 * @param [isSocial] {Boolean} social assets ignore crop entirely
 * @param [isWebpage] {Boolean} webpages fit the visible area instead
 * @return {{left: Number, top: Number, width: Number, height: Number}|null}
 *         null when the asset's dimensions are unknown, so callers can refuse
 *         rather than compute nonsense
 */
export function getAssetRectOnStage({
  metadata,
  scale,
  offset,
  stageDimensions,
  isSocial = false,
  isWebpage = false,
}) {
  const originalWidth = parseFloat(metadata && metadata.width);
  const originalHeight = parseFloat(metadata && metadata.height);

  if (!originalWidth || !originalHeight || !stageDimensions) {
    return null;
  }

  const {
    assetEditorWidth,
    assetEditorHeight,
    assetEditorAR,
    visibleAreaWidth,
  } = stageDimensions;
  const assetAspectRatio = originalWidth / originalHeight;
  const assetScale = isSocial ? 1 : scale || 1;
  const isWiderThanStage = assetAspectRatio > assetEditorAR;

  let width = 0;
  let height = 0;
  let top = 0;
  let left = 0;

  if (isWebpage) {
    width = visibleAreaWidth;
    height = originalHeight * (width / originalWidth);
  } else if (isWiderThanStage) {
    width = assetEditorWidth * assetScale;
    height = width / assetAspectRatio;
  } else {
    height = assetEditorHeight * assetScale;
    width = height * assetAspectRatio;
  }

  if (_.isObject(offset) && !isSocial) {
    top = offset.top * assetEditorHeight;
    left = offset.left * assetEditorWidth;
  } else if (isWiderThanStage) {
    top = assetEditorHeight / 2 - height / 2;
    left = 0;
  } else {
    left = assetEditorWidth / 2 - width / 2;
  }

  return { left, top, width, height };
}

export function getAnalyticsData(asset) {
  const analyticsData = {
    eventPrefix: 'analytics:assetEditor:', // needed for asset modal events
  };

  if (app.assetType.isIMAGE(asset) && !app.assetType.isMAP(asset)) {
    analyticsData.Type = 'Image';
    analyticsData.Source = 'Image Edit';
  }

  if (app.assetType.isGIF(asset)) {
    analyticsData.Type = 'Gif';
    analyticsData.Source = 'Gif Edit';
  } else if (app.assetType.isINFOGRX(asset)) {
    analyticsData.Type = 'Infographics';
    analyticsData.Source = 'Image Edit';
  } else if (app.assetType.isFRAGMENT(asset)) {
    analyticsData.Type = 'Video';
    analyticsData.Source = 'Video Edit';
  }

  return analyticsData;
}

export function getThemeInfo(story) {
  let themeInfo = _.cloneDeep(story.get('_themeInfo'));
  const aspectRatio = story.get('_aspectRatio').name;
  if (_.isArray(themeInfo)) {
    themeInfo = _.head(
      _.filter(themeInfo, {
        aspectRatio,
      })
    );
  }

  const presets = (themeInfo && themeInfo.presets) || [];
  const collagePresets = (themeInfo && themeInfo.collagePresets) || [];
  const animationPresets = _.uniqBy(
    (themeInfo && themeInfo.animationPresets) || [],
    'id'
  );
  const animationOutPresets = _.uniqBy(
    (themeInfo && themeInfo.animationOutPresets) || [],
    'id'
  );
  const transitionPresets = themeInfo && themeInfo.transitionPresets;

  return {
    presets,
    animationPresets,
    collagePresets,
    sceneLayouts: themeInfo.sceneLayouts,
    animationOutPresets,
    wiperType: themeInfo && themeInfo.wiperType,
    hasWiper: themeInfo && themeInfo.hasWiper, // backward compatibility
    hasSuper: themeInfo.hasSuper !== false,
    logoBugImageURL: app.prependHttp(themeInfo.logoBugImageURL),
    showSuperCredit: themeInfo.showSuperCredit,
    showFreeText: themeInfo.showFreeText,
    canApplyTransition: themeInfo.canApplyTransition,
    defaultTransitionPreset: themeInfo.defaultTransitionPreset,
    transitionPresets,
    aspectRatio,
  };
}

export function getDefaultPages() {
  return [
    {
      id: 'preview',
      assetBlacklist: null,
      displayText: 'Preview',
      fields: [],
      icon: null,
      groupBy: [],
      agdAttributes: [],
      custom: false,
      layers: [],
      previewMode: true,
    },
    {
      id: 'audio',
      groupBy: ['AUDIO'],
      displayText: 'Audio',
      fields: [],
      icon: null,
      agdAttributes: [],
      custom: false,
      layers: [],
      previewMode: false,
    },
    {
      id: 'crop',
      groupBy: ['CROP'],
      displayText: 'Crop',
      fields: [],
      icon: null,
      iconClass: 'wticon-Crop',
      agdAttributes: ['offset', 'scale'],
      custom: false,
      layers: [],
      previewMode: false,
    },
    {
      id: 'zoom',
      groupBy: ['ZOOM'],
      displayText: 'Zoom',
      fields: [],
      icon: null,
      iconClass: 'wticon-zoom-in',
      agdAttributes: ['zoomX', 'zoomY', 'zoomX1', 'zoomY1', 'effectType'],
      custom: false,
      layers: [],
      previewMode: false,
    },
    {
      id: 'super',
      groupBy: ['TEXT'],
      displayText: 'Super',
      fields: [],
      icon: app.DEFAULT.customOverlaysIcons.format('super.png'),
      agdAttributes: ['superName', 'superPosition', 'superTitle'],
      custom: false,
      layers: [],
      previewMode: false,
    },
    {
      id: 'credit',
      groupBy: ['TEXT'],
      displayText: 'Credit',
      fields: [],
      icon: app.DEFAULT.customOverlaysIcons.format('credit.png'),
      agdAttributes: ['creditPosition', 'creditText'],
      custom: false,
      layers: [],
      previewMode: false,
    },
    {
      id: 'subtitles',
      groupBy: ['TEXT'],
      displayText: 'Subtitles (Legacy)',
      fields: [],
      icon: app.DEFAULT.customOverlaysIcons.format('subtitles.png'),
      agdAttributes: ['videoQuoteSubTitles'],
      custom: false,
      layers: [],
      previewMode: false,
      canAddNew: false,
    },
    {
      id: 'textOverlay',
      groupBy: ['TEXT'],
      title: 'Your Style',
      displayText: 'Text',
      fields: [],
      icon: app.DEFAULT.customOverlaysIcons.format('free_text_pencil.png'),
      agdAttributes: ['textOverlays'],
      custom: false,
      layers: [],
      previewMode: false,
    },
    {
      id: 'overlay_asset',
      groupBy: ['OVERLAY_ASSET'],
      displayText: '',
      fields: [],
      icon: null,
      agdAttributes: [],
      custom: false,
      layers: [],
      selectedOverlayAssetId: null,
      previewMode: false,
    },
  ];
}

export function checkIfPreviewNeeded(state) {
  if (state.openedFrom !== 'timeline') {
    let type = null;
    const isMap = app.assetType.isMAP(state.workModel);
    const isSocial = app.assetType.isSOCIAL(state.workModel);
    const isSocialVideo = app.assetType.isSOCIALVIDEO(state.workModel);
    const isSocialImage = app.assetType.isSOCIALIMAGE(state.workModel);
    const isSocialTwitter = app.assetType.isTWITTER(state.workModel);

    if (
      /asset-gallery|original-video|timeline-change-segment/.test(
        state.openedFrom
      )
    ) {
      if (isMap) {
        type = app.RealtimePreviewType.VIDEO;
      } else if (isSocialVideo) {
        type = app.RealtimePreviewType.STATIC_MASK;
      } else if (isSocialImage || isSocialTwitter) {
        type = app.RealtimePreviewType.STATIC;
      }
    } else if ('lightbox' === state.openedFrom) {
      if (isSocial) {
        type = app.RealtimePreviewType.VIDEO;
      }
    }

    if (type) {
      workPool.job.createAssetPreview.forExternalAsset({
        asset: state.workModel,
        type,
        programmer: app.storyController.story.getProgrammer().attributes,
      });
    }
  }
}

export function getAssetModalTitle(asset) {
  let title = 'Preview';

  if (app.assetType.isWEBPAGE(asset)) {
    title = 'Webpage Preview';
  } else if (app.assetType.isMAP(asset)) {
    title = 'Map Preview';
  } else if (app.assetType.isIMAGE(asset)) {
    title = 'Image Preview';
  } else if (app.assetType.isFRAGMENT(asset)) {
    title = 'Preview Video Segment';
  } else if (app.assetType.isTWITTER(asset)) {
    title = 'Tweet Preview';
  } else if (app.assetType.isFACEBOOK(asset)) {
    title = 'Facebook Post Preview';
  } else if (app.assetType.isINSTAGRAM(asset)) {
    title = 'Instagram Post Preview';
  } else if (app.assetType.isGIF(asset)) {
    title = 'GIF Preview';
  }

  return title;
}

/**
 * @param fragment {object} asset
 * @returns video {object} asset
 */
export function getOriginalVideoFromFragment(fragment) {
  if (!app.assetType.isFRAGMENT(fragment)) {
    return null;
  }

  let newAssetType = 'VIDEO';
  let newType = app.assetType.VIDEO;
  if (app.assetType.isSOCIALFRAGMENT(fragment)) {
    newAssetType = fragment.metadata.assetType;
    newType = app.assetType.SOCIAL;
  }

  const clone = $.extend(true, {}, fragment, {
    // overwrite
    id: fragment.metadata.assetUUID,
    type: newType,
    score: app.assetScore.EXCELLENT,
    agd: {}, // reset
    metadata: {
      assetType: newAssetType,
      uploadStatus: null,
    },
  });
  const video = _.omit(clone, [
    // omit fragment relevant data
    'previewResultData',
    'realTimePreviewResultData',
    'realTimePreviewChecksum',
    'thumbnail',
    '_exactDuration',
    '_previewOp',
    // omit timeline relevant data
    PictureInPictureAdapter.TRACK_PROP,
    'displayStartTime',
    'displayDuration',
    'displayEndTime',
    'guid',
    'limit',
  ]);

  return video;
}
